/**
 * 使い魔の向き
 */
export type Direction = "up" | "down" | "left" | "right";

/**
 * 使い魔（チビゴーレム）
 * 冒険者が魔導書に綴った呪文に従って動く
 *
 * 純粋なロジック層: 位置・向きの管理と移動ロジックのみ。
 * 描画は FamiliarRenderer が担当。
 * 状態変更時に onChange コールバックで通知。
 */
export class Familiar {
  private _gridX: number;
  private _gridY: number;
  private _direction: Direction;

  /** 状態変更時のコールバック（Rendererが購読） */
  onChange?: () => void;

  constructor(gridX: number, gridY: number, direction: Direction = "up") {
    this._gridX = gridX;
    this._gridY = gridY;
    this._direction = direction;
  }

  get gridX(): number {
    return this._gridX;
  }

  get gridY(): number {
    return this._gridY;
  }

  get direction(): Direction {
    return this._direction;
  }

  /**
   * 指定座標に移動
   */
  moveTo(gridX: number, gridY: number): void {
    this._gridX = gridX;
    this._gridY = gridY;
    this.onChange?.();
  }

  /**
   * 向きを変更
   */
  setDirection(direction: Direction): void {
    this._direction = direction;
    this.onChange?.();
  }

  /**
   * 右に90度回転
   */
  turnRight(): void {
    const nextDirection: Record<Direction, Direction> = {
      up: "right",
      right: "down",
      down: "left",
      left: "up",
    };
    this.setDirection(nextDirection[this._direction]);
  }

  /**
   * 左に90度回転
   */
  turnLeft(): void {
    const nextDirection: Record<Direction, Direction> = {
      up: "left",
      left: "down",
      down: "right",
      right: "up",
    };
    this.setDirection(nextDirection[this._direction]);
  }

  /**
   * 後ろに180度回転
   */
  turnBack(): void {
    const nextDirection: Record<Direction, Direction> = {
      up: "down",
      down: "up",
      left: "right",
      right: "left",
    };
    this.setDirection(nextDirection[this._direction]);
  }

  /**
   * 指定方向に回転（ディスパッチ用）
   */
  turn(direction: "right" | "left" | "back"): void {
    const actions: Record<string, () => void> = {
      right: () => this.turnRight(),
      left: () => this.turnLeft(),
      back: () => this.turnBack(),
    };
    actions[direction]?.();
  }

  /**
   * 現在の向きに1マス前進した場合の座標を取得
   */
  getForwardPosition(): { x: number; y: number } {
    const delta: Record<Direction, { x: number; y: number }> = {
      up: { x: 0, y: -1 },
      down: { x: 0, y: 1 },
      left: { x: -1, y: 0 },
      right: { x: 1, y: 0 },
    };
    return {
      x: this._gridX + delta[this._direction].x,
      y: this._gridY + delta[this._direction].y,
    };
  }
}
